// src/components/SearchResults.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingSpinner, ErrorMessage, EmptyState } from './CommonUI';
import SetCard from './SetCard';
import { useAuth } from './AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const SearchResults = () => {
    const location = useLocation();
    const navigate  = useNavigate();
    const { user }  = useAuth();

    const [searchQuery,    setSearchQuery]    = useState('');
    const [searchResults,  setSearchResults]  = useState([]);
    const [selectedSets,   setSelectedSets]   = useState({});
    // Persistent ownership map: { [set_num]: { inCollection: number, inWishlist: boolean } }
    const [collectionStatus, setCollectionStatus] = useState({});
    const [isLoading,      setIsLoading]      = useState(true);
    const [error,          setError]          = useState(null);

    const searchTitle = useMemo(() => {
        const q = new URLSearchParams(location.search).get('query');
        return q && q.trim() ? `Search: ${q.trim()}` : 'Search';
    }, [location.search]);

    useDocumentTitle(searchTitle);

    // Parse URL query param and fetch results
    useEffect(() => {
        const query = new URLSearchParams(location.search).get('query');
        if (query) {
            setSearchQuery(query);
            fetchSearchResults(query);
        } else {
            setIsLoading(false);
            setSearchResults([]);
            setCollectionStatus({});
        }
        // Reset selection on new search
        setSelectedSets({});
    }, [location.search]);

    const fetchSearchResults = async (query) => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/search_sets.php?query=${encodeURIComponent(query)}`
            );
            const sets = response.data;
            setSearchResults(sets);

            // Seed collectionStatus from API response
            const status = {};
            sets.forEach(set => {
                const inCollection = Number(set.in_collection || 0);
                const inWishlist   = Boolean(Number(set.in_wishlist || 0));
                if (inCollection > 0 || inWishlist) {
                    status[set.set_num] = { inCollection, inWishlist };
                }
            });
            setCollectionStatus(status);
        } catch (err) {
            console.error('Error fetching search results:', err);
            setError('An error occurred while searching. Please try again.');
        }
        setIsLoading(false);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
        }
    };

    // -------------------------------------------------------------------------
    // Selection / quantity helpers
    // -------------------------------------------------------------------------

    const handleQuantityChange = useCallback((setNum, quantity, e) => {
        if (e) e.stopPropagation();
        setSelectedSets(prev => ({ ...prev, [setNum]: quantity }));
    }, []);

    const toggleSelectSet = useCallback((setNum) => {
        setSelectedSets(prev =>
            prev[setNum] !== undefined
                ? (() => { const n = { ...prev }; delete n[setNum]; return n; })()
                : { ...prev, [setNum]: 1 }
        );
    }, []);

    // -------------------------------------------------------------------------
    // Add to collection / wishlist
    // -------------------------------------------------------------------------

    // Merge arbitrary fields into a set's status entry
    const updateStatus = useCallback((setNum, fields) => {
        setCollectionStatus(prev => {
            const cur = prev[setNum] ?? { inCollection: 0, inWishlist: false };
            return { ...prev, [setNum]: { ...cur, ...fields } };
        });
    }, []);

    // Fire-and-forget: remove the set from the wishlist on the server and clear the local badge
    const removeFromWishlistAfterCollect = useCallback((setNum) => {
        if (!user?.user_id) return;
        axios.post(`${process.env.REACT_APP_API_URL}/remove_set_from_wishlist.php`, {
            user_id: user.user_id,
            set_num: setNum,
        }).then(res => {
            if (res.data.success) updateStatus(setNum, { inWishlist: false });
        }).catch(() => {}); // non-critical: badge clears optimistically below
    }, [user, updateStatus]);

    const addToCollection = useCallback((setNum, quantity, e) => {
        if (e) e.stopPropagation();
        // Capture wishlist state before the async call
        const wasInWishlist = collectionStatus[setNum]?.inWishlist ?? false;

        axios.post(`${process.env.REACT_APP_API_URL}/add_to_collection.php`, {
            sets: [{ setNum, quantity }]
        }).then(res => {
            if (res.data.success) {
                updateStatus(setNum, {
                    inCollection: (collectionStatus[setNum]?.inCollection ?? 0) + quantity,
                    ...(wasInWishlist ? { inWishlist: false } : {}),
                });
                setSelectedSets(prev => { const n = { ...prev }; delete n[setNum]; return n; });
                if (wasInWishlist) removeFromWishlistAfterCollect(setNum);
            } else {
                setError('Failed to add set to collection.');
            }
        }).catch(() => setError('An error occurred while adding to collection.'));
    }, [collectionStatus, updateStatus, removeFromWishlistAfterCollect]);

    const addToWishlist = useCallback((setNum, quantity, e) => {
        if (e) e.stopPropagation();
        axios.post(`${process.env.REACT_APP_API_URL}/add_to_wishlist.php`, {
            sets: [{ setNum, quantity }]
        }).then(res => {
            if (res.data.success) {
                updateStatus(setNum, { inWishlist: true });
                setSelectedSets(prev => { const n = { ...prev }; delete n[setNum]; return n; });
            } else {
                setError('Failed to add set to wishlist.');
            }
        }).catch(() => setError('An error occurred while adding to wishlist.'));
    }, [updateStatus]);

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------

    return (
        <div className="max-w-6xl mx-auto px-4 py-8">
            {/* Search Banner */}
            <div className="mb-8 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl shadow-lg overflow-hidden">
                <div className="relative py-10 px-6 md:px-10">
                    <div className="relative z-10 max-w-3xl mx-auto text-center">
                        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Search Results</h1>
                        <p className="text-red-100 text-lg mb-8 max-w-2xl mx-auto">
                            {searchQuery ? `Showing results for "${searchQuery}"` : 'Enter a search term to find LEGO® sets'}
                        </p>

                        <form onSubmit={handleSearchSubmit} className="max-w-2xl mx-auto">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="Search by set name or number..."
                                    className="w-full py-3 px-5 pr-12 rounded-full border-none shadow-md focus:outline-none focus:ring-2 focus:ring-yellow-400 text-gray-800"
                                />
                                <button
                                    type="submit"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center transition-colors shadow-sm"
                                >
                                    <FontAwesomeIcon icon="magnifying-glass" />
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="absolute inset-0 opacity-15" style={{ backgroundImage: 'url(/images/lego_pattern_bg.png)', backgroundSize: '200px' }} />
                    <img
                        src="/images/Logrick_search.webp"
                        alt=""
                        aria-hidden="true"
                        className="absolute top-2 right-2 bottom-auto w-16 sm:top-3 sm:w-20 sm:right-4 md:top-auto md:bottom-2 md:w-24 md:right-8 lg:w-28 lg:right-10 h-auto pointer-events-none select-none drop-shadow-md"
                    />
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="mb-6">
                    <ErrorMessage message={error} onRetry={() => setError(null)} />
                </div>
            )}

            {/* Loading */}
            {isLoading ? (
                <div className="py-12">
                    <LoadingSpinner text="Searching for sets..." size="lg" />
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 md:p-8">
                    {searchResults.length === 0 ? (
                        <EmptyState
                            message={searchQuery ? `No sets found matching "${searchQuery}"` : 'Enter a search term to find LEGO® sets'}
                            actionText="Try Another Search"
                            onAction={() => setSearchQuery('')}
                            icon="search"
                            className="py-12"
                        />
                    ) : (
                        <>
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-xl font-bold text-gray-800">
                                    <FontAwesomeIcon icon="search" className="text-gray-600 mr-2" />
                                    Found {searchResults.length} {searchResults.length === 1 ? 'set' : 'sets'}
                                </h3>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                {searchResults.map(set => (
                                    <SetCard
                                        key={set.set_num}
                                        set={set}
                                        status={collectionStatus[set.set_num] ?? null}
                                        viewMode="grid"
                                        showDetails={false}
                                        selectedQuantity={selectedSets[set.set_num]}
                                        onToggleSelect={toggleSelectSet}
                                        onQuantityChange={handleQuantityChange}
                                        onAddToCollection={addToCollection}
                                        onAddToWishlist={addToWishlist}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchResults;
